/**
 * A background job's progress bar (f8.1).
 *
 * **f8.1 adds no screen**, and this is not one. It is an export f8.2 consumes
 * when it builds the Data import screen, put here because the spine is what
 * this feature delivers and a progress bar invented per screen is how three
 * screens end up with three of them.
 *
 * Every colour is a token. The track is `var(--border)`, the fill is
 * `var(--primary)`, the corners are `var(--radius-full)`, and the terminal
 * states reuse the project's own pill classes -- `pill-success`,
 * `pill-danger`, `pill-warning` -- rather than introducing colours of their
 * own. No brand hex appears in this file, so light and dark both work without
 * a second stylesheet.
 *
 * **It says nothing it does not know.** When the server has not yet counted
 * the input, `progress_percent` is 0 and the bar renders indeterminate with
 * the message beside it -- because a bar that moves without meaning is worse
 * than a bar that waits. Once a total is known it renders the real fraction
 * and the row counts, which is what makes a 14,197-row parse legible rather
 * than merely slow.
 */

import { AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import type { Job } from '@/api/client';
import { isJobTerminal } from '@/api/queries';

/** How a state is spoken about, and which pill carries it. */
const PRESENTATION: Record<string, { label: string; pill: string }> = {
  QUEUED: { label: 'Queued', pill: 'pill-neutral' },
  RUNNING: { label: 'Running', pill: 'pill-info' },
  SUCCEEDED: { label: 'Finished', pill: 'pill-success' },
  FAILED: { label: 'Failed', pill: 'pill-danger' },
  RETRY_SCHEDULED: { label: 'Retrying', pill: 'pill-warning' },
};

function presentationOf(state: string) {
  // An unrecognised state renders neutrally with its own code rather than
  // breaking the screen: a later phase may seed a sixth, and a bar is not the
  // place to discover it.
  return PRESENTATION[state] ?? { label: state, pill: 'pill-neutral' };
}

function StateIcon({ state }: { state: string }) {
  if (state === 'SUCCEEDED') return <CheckCircle2 aria-hidden="true" />;
  if (state === 'FAILED') return <AlertTriangle aria-hidden="true" />;
  if (state === 'RUNNING') return <Loader2 className="job-progress-spin" aria-hidden="true" />;
  return <Clock aria-hidden="true" />;
}

export interface JobProgressProps {
  job: Job;
  /** A caption above the bar, e.g. the file being read. */
  title?: string;
}

export function JobProgress({ job, title }: JobProgressProps) {
  const { label, pill } = presentationOf(job.state);
  const finished = isJobTerminal(job.state);
  const knowsTotal = typeof job.rows_total === 'number' && job.rows_total > 0;
  const percent = finished && job.state === 'SUCCEEDED' ? 100 : job.progress_percent;

  return (
    <div className="job-progress">
      <div className="job-progress-head">
        {title ? <span className="job-progress-title">{title}</span> : null}
        <span className={`badge ${pill}`}>
          <StateIcon state={job.state} />
          {label}
        </span>
      </div>

      <div
        className="job-progress-track"
        role="progressbar"
        aria-label={title ? `${title} progress` : 'Import progress'}
        /* The ARIA value is omitted entirely while the total is unknown, which
           is how an indeterminate bar is spelled -- rather than reporting 0%,
           which a screen reader would announce as real and stalled. */
        aria-valuenow={knowsTotal ? percent : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={knowsTotal ? `${percent}%` : job.progress_message ?? 'Working'}
      >
        <div
          className={`job-progress-fill${knowsTotal ? '' : ' job-progress-indeterminate'}`}
          style={{ width: knowsTotal ? `${percent}%` : undefined }}
        />
      </div>

      <div className="job-progress-foot">
        {knowsTotal ? (
          <span className="job-progress-counts">
            {job.rows_processed.toLocaleString()} of{' '}
            {(job.rows_total as number).toLocaleString()} rows
          </span>
        ) : (
          <span className="job-progress-message">
            {job.progress_message ?? 'Working…'}
          </span>
        )}
        {job.attempts > 1 ? (
          <span className="job-progress-attempt">
            Attempt {job.attempts} of {job.max_attempts}
          </span>
        ) : null}
      </div>

      {job.state === 'FAILED' && job.error_message ? (
        <p className="job-progress-error" role="alert">
          {job.error_message}
        </p>
      ) : null}
    </div>
  );
}
